import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  useMediaQuery,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  styled,
  Chip,
  Snackbar,
  LinearProgress,
  FormControl,
  Autocomplete,
  TextField
} from '@mui/material';
import dayjs from "dayjs";
import DateUtils from '../../Helpers/DateUtils';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { SingleInputDateRangeField } from "@mui/x-date-pickers-pro/SingleInputDateRangeField";
import { DesktopDateRangePicker } from "@mui/x-date-pickers-pro/DesktopDateRangePicker";
import { MobileDateRangePicker } from "@mui/x-date-pickers-pro/MobileDateRangePicker";
import SendIcon from '@mui/icons-material/Send';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Navbar from '../app/Navbar';
import { Checkbox, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';

const API_BASE_URL = process.env.REACT_APP_API_BASEURL;

// Custom styled input for accessibility
const VisuallyHiddenInput = styled('input')({
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  border: 0,
  whiteSpace: 'nowrap',
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  overflow: 'hidden',
});

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#f50057' },
  },
});

const VALID_EXTENSIONS = ['.csv', '.xls', '.xlsx', '.html'];
const banks = ['HDFC BANK', 'GPAY'];

const BulkStatementProcesser = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const familyMembers = JSON.parse(localStorage.getItem("familymembers"));
  const user = localStorage.getItem("user");
  const householdName = localStorage.getItem("householdName");
  const [values, setValues] = useState({
    bankName: 'HDFC Bank',
    householdName: householdName,
    accountHolder: user,
    fromDate: dayjs().startOf('month').format('YYYY-MM-DD'),
    toDate: dayjs().endOf('month').format('YYYY-MM-DD'),
    fileName: ''
  });
  const [selectedExpenses, setSelectedExpenses] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedExpense, setEditedExpense] = useState(null);
  const isDeviceDesktop = useMediaQuery("(min-width:600px)");

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      const filename = selected.name;
      const ext = filename.slice(filename.lastIndexOf('.')).toLowerCase();
      if (!VALID_EXTENSIONS.includes(ext)) {
        setError(`Unsupported file type. Please upload one of: ${VALID_EXTENSIONS.join(', ')}`);
        setFile(null);
      } else {
        setError('');
        setFile(selected);
        setValues(prev => ({ ...prev, fileName: filename }));
      }
    }
  };

  const reloadOnNewDateRangeSelection = (inputDateRange) => {
    const str = inputDateRange.toString();
    const dateRegex = /\w{3}, \d{1,2} \w{3} \d{4} \d{2}:\d{2}:\d{2} GMT/g;
    const matched = str.match(dateRegex) || [];
    if (matched.length === 2) {
      const from = DateUtils.convertDateToLocalTimeZone(matched[0]);
      const to = DateUtils.convertDateToLocalTimeZone(matched[1]);
      setValues(prev => ({ ...prev, fromDate: from, toDate: to }));
    } else {
      alert("Please select a valid date range.");
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  function pluck(array, key) {
    return array.map(o => o[key]);
  }

  // Upload & parse CSV/XLSX/HTML
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setLoading(true);
    setUploading(true);
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('csvFile', file);  // Use expected field name for multer
    formData.append('bankName', values.bankName);
    formData.append('fromDate', values.fromDate);
    formData.append('toDate', values.toDate);
    try {
      const response = await fetch(`${API_BASE_URL}/api/expenses/upload`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': localStorage.getItem('token'),
          'x-api-key': process.env.REACT_APP_LICENCE
        }
      });

      const contentType = response.headers.get('content-type') || '';
      let result;
      if (contentType.includes('application/json')) {
        result = await response.json();
      } else {
        const text = await response.text();
        throw new Error(text || 'Unexpected response from server');
      }
      if (!response.ok) {
        throw new Error(result.error || result.message || 'Failed to process file');
      }

      // Deduplicate entries
      const rawExpenses = result.data;
      const map = new Map();
      rawExpenses.forEach(item => {
        const key = `${item.expensedate}|${item.amount}|${item.description}`;
        if (!map.has(key)) map.set(key, item);
      });
      const uniqueExpenses = Array.from(map.values());
      setExpenses(uniqueExpenses);
      setSelectedExpenses(uniqueExpenses.map((_, index) => index)); // Add this line

      const dupCount = rawExpenses.length - uniqueExpenses.length;
      if (dupCount > 0) showSnackbar(`${dupCount} duplicate entries removed`, 'warning');

      setSuccess(`Parsed and listed ${uniqueExpenses.length} entries. Verify and save.`);
      showSnackbar(`Listed ${uniqueExpenses.length} entries`, 'success');

    } catch (err) {
      setError(err.message);
      showSnackbar(err.message, 'error');
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  // Bulk-create expenses
  const handleCreateExpenses = async () => {
    if (!selectedExpenses.length) {
      setError('Please select expenses to save');
      return;
    }

    setSaving(true);
    setLoading(true);
    try {
      const selectedExpensesData = expenses.filter((_, index) => selectedExpenses.includes(index));
      
      const body = {
        filename: values.fileName,
        accountHolder: values.accountHolder,
        fromDate: values.fromDate,
        toDate: values.toDate,
        bankName: values.bankName,
        householdName: values.householdName,
        expenses: selectedExpensesData // This is correct, using filtered data
      };

      const response = await fetch(`${API_BASE_URL}/api/expenses/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('token'),
          'x-api-key': process.env.REACT_APP_LICENCE
        },
        body: JSON.stringify(body),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Failed to save expenses');

      // Fix these lines to use selectedExpensesData.length instead of expenses.length
      setSuccess(`Successfully saved ${selectedExpensesData.length} expenses!`);
      showSnackbar(`Saved ${selectedExpensesData.length} expenses!`, 'success');

      // Optionally clear selection after save
      setSelectedExpenses([]);
      
      // Remove saved items from the list
      const remainingExpenses = expenses.filter((_, index) => !selectedExpenses.includes(index));
      setExpenses(remainingExpenses);

    } catch (err) {
      setError('Error creating expenses: ' + err.message);
      showSnackbar('Error: ' + err.message, 'error');
    } finally {
      setLoading(false);
      setSaving(false);
    }
  };

  // Add these handler functions before the return statement
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedExpenses(expenses.map((_, index) => index));
    } else {
      setSelectedExpenses([]);
    }
  };

  const handleSelectRow = (idx) => {
    setSelectedExpenses(prev => {
      if (prev.includes(idx)) {
        return prev.filter(i => i !== idx);
      } else {
        return [...prev, idx];
      }
    });
  };

  const handleEdit = (expense, idx) => {
    setEditingIndex(idx);
    setEditedExpense({ ...expense });
  };

  const handleSaveEdit = () => {
    setExpenses(prev => prev.map((exp, idx) =>
      idx === editingIndex ? editedExpense : exp
    ));
    setEditingIndex(null);
    setEditedExpense(null);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditedExpense(null);
  };

  const handleEditChange = (e, field) => {
    setEditedExpense(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  return (
    <ThemeProvider theme={theme}>
      <Navbar />
      <Typography sx={{ mt: 2 }} textAlign="center" variant="h5">
        BULK STATEMENT PROCESSOR
      </Typography>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
          <Typography variant="h5" gutterBottom>Upload Bank or Activity Statement</Typography>
          <br />

          <FormControl fullWidth>
            <Autocomplete
              disablePortal
              name="Bank Name"
              options={banks}
              value={values.bankName}
              onChange={(_, newVal) => setValues(prev => ({ ...prev, bankName: newVal }))}
              renderInput={(params) => <TextField label="Bank Name" {...params} />}
            />
          </FormControl>
          <br /><br />
          <FormControl fullWidth>
            <Autocomplete
              disablePortal
              name="Account Holder"
              options={pluck(familyMembers, "member_name")}
              value={values.accountHolder}
              onChange={(_, newVal) => setValues(prev => ({ ...prev, accountHolder: newVal }))}
              renderInput={(params) => <TextField label="Account Holder" {...params} />}
            />
          </FormControl>
          <br /><br />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            {isDeviceDesktop ? (
              <DesktopDateRangePicker
                calendars={1}
                timeZone="local"
                format="DD-MMM-YYYY"
                sx={{ width: '350px' }}
                slotProps={{ actionBar: { actions: [] } }}
                slots={{ field: SingleInputDateRangeField }}
                defaultValue={[dayjs().startOf('month'), dayjs().endOf('month')]}
                onAccept={reloadOnNewDateRangeSelection}
              />
            ) : (
              <MobileDateRangePicker
                calendars={1}
                timeZone="local"
                format="DD-MMM-YYYY"
                sx={{ width: '350px' }}
                slotProps={{ toolbar: { hidden: true } }}
                slots={{ field: SingleInputDateRangeField }}
                defaultValue={[dayjs().startOf('month'), dayjs().endOf('month')]}
                onAccept={reloadOnNewDateRangeSelection}
              />
            )}
          </LocalizationProvider>
          <br /><br />
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Button component="label" variant="contained" startIcon={<FileUploadIcon />} disabled={uploading}>
              {file ? 'Change File' : 'Select Statement File'}
              <VisuallyHiddenInput
                type="file"
                accept={VALID_EXTENSIONS.join(',')}
                onChange={handleFileChange}
              />
            </Button>
            {file && <Chip label={file.name} variant="outlined" onDelete={() => setFile(null)} disabled={uploading} />}
          </Box>
          <Button variant="contained" color="primary" onClick={handleUpload} disabled={uploading || !file} startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}>
            {uploading ? 'Uploading...' : 'List Expenses'}
          </Button>
          {uploading && <Box sx={{ width: '100%', mt: 2 }}><LinearProgress /></Box>}
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
        </Paper>
        <Alert severity="info" sx={{ mb: 2 }}>Important: Supports CSV, XLS, XLSX, and HTML statements.</Alert>
        {expenses.length > 0 && (
          <Paper elevation={3} sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom>Processed Expenses</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Found {expenses.length} expenses</Typography>
              <Button 
                variant="contained" 
                color="secondary" 
                onClick={handleCreateExpenses} 
                disabled={saving} 
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveAltIcon />}
              >
                {saving ? 'Saving...' : `Save Selected (${selectedExpenses.length})`}
              </Button>
            </Box>
            {saving && <Box sx={{ width: '100%', mb: 2 }}><LinearProgress /></Box>}
            // Replace the existing Table section
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table aria-label="expenses table">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedExpenses.length > 0 && selectedExpenses.length < expenses.length}
                        checked={expenses.length > 0 && selectedExpenses.length === expenses.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expenses.map((exp, idx) => (
                    <TableRow key={idx}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedExpenses.includes(idx)}
                          onChange={() => handleSelectRow(idx)}
                        />
                      </TableCell>
                      {editingIndex === idx ? (
                        <>
                          <TableCell>
                            <TextField
                              type="date"
                              value={editedExpense.expensedate}
                              onChange={(e) => handleEditChange(e, 'expensedate')}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={editedExpense.description}
                              onChange={(e) => handleEditChange(e, 'description')}
                              size="small"
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              value={editedExpense.amount}
                              onChange={(e) => handleEditChange(e, 'amount')}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={editedExpense.category}
                              onChange={(e) => handleEditChange(e, 'category')}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton onClick={handleSaveEdit} color="primary" size="small">
                              <DoneIcon />
                            </IconButton>
                            <IconButton onClick={handleCancelEdit} color="error" size="small">
                              <CloseIcon />
                            </IconButton>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>{exp.expensedate}</TableCell>
                          <TableCell sx={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {exp.description}
                          </TableCell>
                          <TableCell align="right">{parseFloat(exp.amount).toFixed(2)}</TableCell>
                          <TableCell>{exp.category}</TableCell>
                          <TableCell align="center">
                            <IconButton onClick={() => handleEdit(exp, idx)} color="primary" size="small">
                              <EditIcon />
                            </IconButton>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
        </Snackbar>
      </Container>
    </ThemeProvider>
  );
};

export default BulkStatementProcesser;
